/**
 * O estado do cliente inteiro, num sítio só.
 *
 * A forma é a de uma base de dados normalizada em memória: dicionários por id,
 * nunca listas aninhadas. O gateway manda deltas (`ChannelUpdate` com um
 * `Partial<Channel>`), e aplicar um delta a uma árvore aninhada obriga a
 * reescrever o caminho todo — é aí que aparecem os bugs de "a mensagem entrou
 * no canal errado". Com dicionários, cada evento toca exactamente uma chave.
 *
 * As listas (ordem dos canais, membros por role) são calculadas em `selectors`,
 * não guardadas — estado derivado guardado é estado que fica dessincronizado.
 */

import type {
  Channel,
  Emoji,
  GatewayEvent,
  Member,
  Message,
  Mute,
  MuteScope,
  PublicUser,
  ReadState,
  ReadyPayload,
  Relationship,
  Role,
  SelectableStatus,
  SelfUser,
  Server,
  Snowflake,
  SpaceChatLine,
  SpaceOccupant,
  VoiceState,
} from "@greenvoice/shared";
import { KNOCK_TTL_MS, SPACE_CHAT_HISTORY, TYPING_TTL_MS } from "@greenvoice/shared";

import type { BolaNoAr } from "../components/officeBolaDeNeve";

/** Para as bolas de neve atiradas no mesmo milissegundo terem ids diferentes. */
let contadorDeBolas = 0;

import { create } from "zustand";
import { ApiError, api } from "../lib/api";
import { GatewayClient, type GatewayStatus } from "../lib/gateway";
import { idCompare, idGreater } from "../lib/ids";
import { loadLayoutPrefs, saveLayoutPrefs } from "../lib/layoutPrefs";
import { matchesMobile } from "../lib/media";
import { playSound } from "../lib/sounds";
import type { ServerSettingsTab } from "./serverSettingsTab";

/** Chave composta dos membros: um utilizador é membro de N servidores. */
export const memberKey = (serverId: Snowflake, userId: Snowflake): string => `${serverId}:${userId}`;

/** Chave composta dos silêncios: o alvo sozinho não diz se é canal ou servidor. */
export const muteKey = (scope: MuteScope, targetId: Snowflake): string => `${scope}:${targetId}`;

export interface HistoryState {
  loading: boolean;
  /** Já chegámos ao início do canal — não há mais nada para trás. */
  atStart: boolean;
  /**
   * A lista chega à mensagem mais recente do canal.
   *
   * É verdade em tudo menos num caso: saltou-se para um resultado da procura e
   * o que está carregado é uma janela do meio do histórico. A diferença importa
   * porque uma mensagem nova NÃO se pode acrescentar ao fim dessa janela — entre
   * as duas está tudo o que não foi carregado, e colá-las inventava uma conversa
   * que nunca existiu.
   */
  atEnd: boolean;
  /** Já foi carregado uma vez; distingue "vazio" de "ainda não pedi". */
  loaded: boolean;
  error: string | null;
}

const EMPTY_HISTORY: HistoryState = { loading: false, atStart: false, atEnd: true, loaded: false, error: null };

/**
 * Uma batida à porta — a MESMA entrada serve as duas vistas: quem está dentro vê
 * o pedido (aceitar/recusar), quem bateu vê o estado do seu. Vive numa lista e
 * não num dicionário por sala porque é uma notificação: duas pessoas podem bater
 * à mesma porta ao mesmo tempo e nenhuma pode apagar a outra.
 */
/** A ligação privada em que estou (a tocar ou em curso), vista do meu lado. */
export interface LigacaoAtual {
  id: string;
  spaceId: Snowflake;
  /** A outra ponta. */
  outro: Snowflake;
  /** `true` = fui eu que liguei. */
  liguei: boolean;
  estado: "a_tocar" | "em_curso";
  /** Epoch ms do último passo (começou a tocar, ou foi atendida). */
  desde: number;
}

/** Como acabou a última ligação — o cartão mostra-o uns segundos. */
export interface FimDeLigacao {
  outro: Snowflake;
  motivo: Extract<GatewayEvent, { type: "LigacaoTerminada" }>["motivo"];
  liguei: boolean;
  at: number;
}

export interface KnockNotice {
  spaceId: Snowflake;
  roomId: string;
  /** Quem bateu. */
  userId: Snowflake;
  /** Epoch ms da última mexida (batida ou resposta) — é daqui que corre o TTL. */
  at: number;
  /** `null` = ainda sem resposta; depois, o veredicto de quem estava dentro. */
  accept: boolean | null;
}

/** Uma batida respondida desaparece depressa: já cumpriu o que tinha a dizer. */
const KNOCK_ANSWERED_TTL_MS = 4_000;

/** A INTENÇÃO de estar numa call: em que canal entrei e com que direitos. Se
 *  está mesmo LIGADA é outra pergunta, e quem a responde é o motor de voz
 *  (`voiceEngine.getSnapshot().state`) — é ele que tem o media. Houve aqui um
 *  `connected: boolean` que duplicava essa resposta e ficava para trás numa
 *  queda: a UI mostrava "Ligado" com os controlos todos inertes por baixo. */
export interface VoiceSession {
  channelId: Snowflake;
  serverId: Snowflake | null;
  selfMute: boolean;
  selfDeaf: boolean;
  screenSharing: boolean;
  cameraOn: boolean;
  capabilities: { canSpeak: boolean; canVideo: boolean; canShareScreen: boolean; canRecord: boolean };
  error: string | null;
}

export interface AppState {
  // ── sessão ────────────────────────────────────────────────
  self: SelfUser | null;
  authStatus: "unknown" | "anonymous" | "authenticated";
  connectionStatus: GatewayStatus;
  connectionDetail: string | null;
  ready: boolean;

  // ── entidades ─────────────────────────────────────────────
  servers: Record<Snowflake, Server>;
  channels: Record<Snowflake, Channel>;
  roles: Record<Snowflake, Role>;
  members: Record<string, Member>;
  users: Record<Snowflake, PublicUser>;
  readStates: Record<Snowflake, ReadState>;
  /**
   * Silêncios activos, por `muteKey(scope, targetId)` — um canal e um servidor
   * podiam em teoria partilhar id, e a chave composta acaba com a dúvida.
   *
   * Só entram os que ainda valem: o servidor não manda expirados e um temporário
   * é apagado daqui pelo relógio quando chega a hora (`scheduleMuteExpiry`). Um
   * silêncio de uma hora que continuasse a dizer «silenciado» às duas era uma
   * mentira sobre uma coisa que a pessoa pediu justamente por ter fim.
   */
  mutes: Record<string, Mute>;
  /**
   * Emojis personalizados de TODOS os servidores, num dicionário só por id.
   *
   * Não é `Record<serverId, Emoji[]>` de propósito: quem mais lê isto é o texto
   * de uma mensagem, que traz `<:festa:123>` e só tem o id — com a lista por
   * servidor, desenhar uma linha de conversa obrigava a varrer todos os
   * servidores à procura do dono. Por servidor (a grelha das definições e o
   * seletor) é a consulta rara, e essa filtra-se em `emojisOf`.
   */
  emojis: Record<Snowflake, Emoji>;
  voiceStates: Record<Snowflake, VoiceState>;
  messages: Record<Snowflake, Message[]>;
  history: Record<Snowflake, HistoryState>;
  /** canal → utilizador → instante em que o indicador expira. */
  typing: Record<Snowflake, Record<Snowflake, number>>;
  /**
   * Relações por id do OUTRO utilizador (amigos, pedidos, bloqueios). Não vêm no
   * Ready — pedem-se à parte em `loadRelationships`; os eventos mantêm-nas vivas.
   */
  relationships: Record<Snowflake, Relationship>;

  // ── UI ────────────────────────────────────────────────────
  activeServerId: Snowflake | null;
  activeChannelId: Snowflake | null;
  lastChannelByServer: Record<string, Snowflake>;
  /**
   * Mensagem a que se acabou de saltar. A lista rola até lá e pisca-a uma vez;
   * quem a consome limpa-a a seguir, senão a mesma mensagem voltava a piscar
   * sempre que a lista se redesenha.
   */
  jumpTarget: Snowflake | null;
  /**
   * Painel de membros. No desktop é uma coluna (aberta por omissão); num ecrã
   * estreito é a GAVETA da direita — a mesma bandeira, o mesmo botão, layout
   * diferente. Daí arrancar fechada no telemóvel: uma coluna que nasce aberta é
   * uma comodidade, uma gaveta que nasce aberta é um obstáculo.
   */
  memberListOpen: boolean;
  /**
   * A gaveta da ESQUERDA (rail de servidores + canais + voz + perfil). Só tem
   * significado num ecrã estreito: no desktop essa coluna está sempre lá e esta
   * bandeira nunca é lida.
   */
  navOpen: boolean;
  /**
   * Coluna de canais RECOLHIDA (só no desktop — num ecrã estreito ela já é uma
   * gaveta, e recolher uma gaveta não quer dizer nada).
   *
   * É irmã do `memberListOpen` e não a mesma coisa: aquela abre e fecha a
   * coluna da direita, esta encolhe a da esquerda a uma tira com o essencial
   * (a seta, a chamada, o meu microfone). A diferença está no que sobra —
   * ninguém fica sem o botão de sair da chamada por ter arrumado a lista de
   * salas, que é o defeito que a `VoiceBar` existe para não ter.
   *
   * Vem do `localStorage` (é do posto de trabalho, ver `layoutPrefs`), por isso
   * o arranque já a lê recolhida se foi assim que ficou da última vez.
   */
  sidebarCollapsed: boolean;
  voice: VoiceSession | null;
  /** Modo de visão do servidor activo: o chat clássico ou o escritório 2D. */
  viewMode: "chat" | "office";
  /**
   * Qual aba das definições do servidor está aberta, se alguma.
   *
   * Vive aqui e não dentro da barra de canais porque deixou de ser só dela: o
   * cartão de perfil (que é um portal, pendurado no `body`) precisa de abrir a
   * aba dos cargos, e não tem como alcançar o estado de um componente que nem é
   * seu antepassado.
   */
  serverSettingsTab: ServerSettingsTab | null;
  /**
   * O ecrã da chamada do escritório está no CARTÃO DE CANTO (e não a ocupar o
   * ecrã todo)? Arranca minimizado: entrar numa sala abre parte do GreenVoice
   * por cima do mundo, nunca o ecrã inteiro.
   *
   * Vive aqui e não no `OfficeSpace` porque deixou de ser assunto só dele: a
   * barra de cima precisa de o LER (para saber que a porta de volta é o mundo,
   * não o chat) e de lhe MEXER (é ela a única porta de volta agora). Um estado
   * local mais um sincronizador seriam duas verdades a divergir.
   */
  officeCallMin: boolean;
  /**
   * …e está mesmo a TAPAR o mundo. Não é o inverso do de cima: também conta se
   * há chamada, se ela é deste escritório e se o cartão não está escondido —
   * contas que só o `OfficeSpace` sabe fazer, e por isso é ele quem escreve
   * aqui. É esta a bandeira que decide o que a barra de cima diz.
   */
  officeCallFull: boolean;
  /**
   * A procura de mensagens está aberta?
   *
   * Vive aqui porque a procura passou a ser DUAS peças em sítios diferentes da
   * árvore — a caixa no cabeçalho do canal e os resultados na coluna da direita,
   * como no Discord —, e elas não são parentes: o `ChatView` e o `AppShell` são
   * irmãos. Enquanto foi um modal dentro do chat, um `useState` local chegava.
   */
  searchOpen: boolean;
  /**
   * Ocupantes do escritório 2D por espaço: `espaço → utilizador → posição`.
   * É a última posição CONHECIDA de cada remoto — o componente Phaser lê-a a
   * cada frame e interpola até ela, por isso vive aqui e não em estado React
   * (não pode causar re-render a 10Hz). O próprio jogador nunca entra neste
   * mapa: a sua posição é a fonte, não uma cópia a reconciliar.
   */
  positions: Record<Snowflake, Record<Snowflake, SpaceOccupant>>;

  /**
   * Portas trancadas por espaço: `espaço → sala → quem a trancou`. Mesma razão
   * de viver aqui e não em estado React: o loop do Phaser consulta-o a 60fps
   * para o avatar bater na porta. É efémero — o servidor é a autoridade e
   * reenvia tudo no `SpaceState` a cada entrada.
   */
  roomLocks: Record<Snowflake, Record<string, Snowflake>>;

  /**
   * O que cada um "disse" em voz alta no espaço: `espaço → utilizador → texto`.
   * Uma entrada nova substitui a anterior (só há um balão por pessoa) e ninguém
   * a apaga: o loop do Phaser descarta-a passado `SAY_TTL_MS`. Vive aqui e não
   * em estado React porque é o Phaser que a desenha, a 60fps.
   */
  spaceSays: Record<Snowflake, Record<Snowflake, { text: string; at: number }>>;

  /**
   * O chat do escritório (tecla T): as últimas frases de cada espaço, da mais
   * antiga para a mais recente. Chega inteiro no `SpaceState` e cresce a cada
   * `SpaceSay`, cortado a `SPACE_CHAT_HISTORY`.
   */
  spaceChat: Record<Snowflake, SpaceChatLine[]>;

  /**
   * Quem levou um toque, e de quem: `espaço → ALVO → { by, at }`. Indexado pelo
   * ALVO e não por quem chamou, porque a pergunta que o desenho faz a cada frame
   * é "este avatar está a estremecer?" — e essa é sobre quem recebeu.
   *
   * Mesma economia do `spaceSays`: uma entrada substitui a anterior, ninguém a
   * apaga, e o loop do Phaser deixa de a desenhar passado `POKE_TTL_MS`.
   */
  spacePokes: Record<Snowflake, Record<Snowflake, { by: Snowflake; at: number }>>;

  /**
   * As bolas de neve atiradas em cada escritório, as mais recentes no fim (no
   * máximo 16). O Phaser desenha cada uma uma vez, pelo `id`, e esquece-a.
   */
  spaceBolas: Record<Snowflake, BolaNoAr[]>;

  /**
   * Batidas à porta por responder (e as respondidas, por uns segundos). Ao
   * contrário das anteriores, ESTA é renderizada por React — daí a necessidade
   * de `pruneKnocks`, que o `positions`/`spaceSays` não têm.
   */
  knocks: KnockNotice[];

  /** A ligação privada a tocar ou em curso, se houver — ver `LigacaoPainel`. */
  ligacao: LigacaoAtual | null;
  /** Como acabou a última ligação, para o aviso de uns segundos. */
  ligacaoFim: FimDeLigacao | null;
  limparFimDeLigacao(): void;

  /**
   * A correcção que o servidor mandou à NOSSA posição, por espaço — o único caso
   * em que a posição própria volta pela rede. Só é escrita quando um passo é
   * recusado (andar contra uma porta que trancou no mesmo instante em que a
   * atravessávamos): sem isto o cliente continuava a andar dentro de uma sala
   * onde o servidor já não o deixa estar, invisível a toda a gente — o género de
   * avaria que parece que está tudo bem. O objecto é novo a cada correcção, e é
   * por essa IDENTIDADE que o loop do Phaser sabe que é uma nova e a aplica.
   */
  spaceCorrection: Record<Snowflake, SpaceOccupant>;

  // ── acções ────────────────────────────────────────────────
  bootstrap(): Promise<void>;
  signedIn(self: SelfUser): void;
  signOut(): Promise<void>;
  connect(): void;
  disconnect(): void;

  applyReady(payload: ReadyPayload): void;
  applyEvent(event: GatewayEvent): void;
  loadRelationships(): Promise<void>;

  selectServer(serverId: Snowflake | null): void;
  selectChannel(channelId: Snowflake): void;
  /** Abre (ou reabre) a conversa privada com alguém e salta para lá. */
  openDirectMessage(userId: Snowflake): Promise<void>;
  toggleMemberList(): void;
  toggleNav(): void;
  /** Recolhe (ou devolve) a coluna de canais, e grava a escolha neste browser. */
  toggleSidebar(): void;
  /** Fecha as duas gavetas de uma vez (escolheu-se um canal, tocou-se no véu, Esc). */
  closeDrawers(): void;
  setViewMode(mode: "chat" | "office"): void;
  /** Abre as definicoes do servidor na aba pedida. `null` fecha. */
  openServerSettings(tab: ServerSettingsTab | null): void;
  setOfficeCallMin(min: boolean): void;
  setOfficeCallFull(full: boolean): void;
  toggleSearch(): void;
  closeSearch(): void;

  loadMessages(
    channelId: Snowflake,
    options?: { before?: Snowflake; after?: Snowflake; around?: Snowflake },
  ): Promise<void>;
  /**
   * Abre o canal na mensagem indicada, carregando a janela à volta dela se for
   * preciso. É o que a procura faz ao clicar num resultado, e o que uma resposta
   * citada faz ao clicar na citação.
   */
  jumpToMessage(channelId: Snowflake, messageId: Snowflake): Promise<void>;
  clearJumpTarget(): void;
  sendMessage(
    channelId: Snowflake,
    content: string,
    options?: { replyToId?: Snowflake | null; attachmentIds?: Snowflake[] },
  ): Promise<void>;
  editMessage(channelId: Snowflake, messageId: Snowflake, content: string): Promise<void>;
  deleteMessage(channelId: Snowflake, messageId: Snowflake): Promise<void>;
  toggleReaction(channelId: Snowflake, messageId: Snowflake, emoji: string): Promise<void>;
  markRead(channelId: Snowflake): void;

  /**
   * Silenciar. `minutes` conta a partir de agora; `null` é para sempre. Quem
   * carimba a data é o servidor — daqui vai a duração, nunca uma data.
   */
  setMute(scope: MuteScope, targetId: Snowflake, minutes: number | null): Promise<void>;
  clearMute(scope: MuteScope, targetId: Snowflake): Promise<void>;

  /**
   * Emojis. Passam pelo store — e não pela API directamente, como os cargos —
   * porque não é só o ecrã das definições que os lê: qualquer mensagem no ecrã
   * com um `<:festa:123>` desenha-se a partir daqui, e um emoji criado tinha de
   * esperar pelo evento do gateway para a conversa atrás da janela o mostrar.
   */
  loadEmojis(serverId: Snowflake): Promise<void>;
  createEmoji(serverId: Snowflake, name: string, fileId: Snowflake): Promise<Emoji>;
  renameEmoji(serverId: Snowflake, emojiId: Snowflake, name: string): Promise<Emoji>;
  deleteEmoji(serverId: Snowflake, emojiId: Snowflake): Promise<void>;

  /**
   * Tópicos. Não vêm no Ready — pedem-se por canal, ao entrar nele, como o
   * histórico (o porquê está em `ready.service.ts`). Vivem no dicionário dos
   * canais e não num seu, porque um tópico É um canal: qualquer coisa que já
   * saiba desenhar um canal — o não-lido, o silenciar, as menções — passa a
   * saber desenhar um tópico sem uma linha a mais.
   *
   * Não há `deleteThread`: fecha-se pela mesma rota com que se apaga um canal,
   * e é o servidor que sabe que ali o dono do tópico também pode.
   */
  loadThreads(channelId: Snowflake): Promise<void>;
  createThread(channelId: Snowflake, name: string, messageId?: Snowflake | null): Promise<Channel>;

  notifyTyping(channelId: Snowflake): void;
  pruneTyping(): void;
  pruneKnocks(): void;

  setPresence(status: SelectableStatus, statusText?: string | null): void;
  setVoice(patch: Partial<VoiceSession> | null): void;
}

/**
 * Uma ligação por aba, criada à primeira necessidade. Vive fora do store de
 * propósito: é um recurso (socket, timers), não estado renderizável, e um
 * `set()` nunca lhe pode mexer por engano.
 */
let gateway: GatewayClient | undefined;

/**
 * Canais a que já se pediram os tópicos nesta ligação.
 *
 * Fora do store porque nada no ecrã depende de "já pedi": é só a memória que
 * impede um GET a cada vez que se volta ao mesmo canal. Esvazia-se no Ready
 * (uma reconexão pode trazer tópicos novos ou fechados) e ao sair.
 */
const threadsRequested = new Set<Snowflake>();

/**
 * Nova tentativa de arranque, quando a sessão não pôde ser LIDA (API em baixo,
 * rede) — que é diferente de não existir. Mesma razão de viver fora do store que
 * o socket: é um timer, não estado. O escalão trava nos 15s porque a seguir a
 * isso já não é uma falha passageira e insistir de meio em meio segundo só
 * aquece a máquina.
 */
const BOOT_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000] as const;
let bootRetry: ReturnType<typeof setTimeout> | undefined;
let bootAttempt = 0;

function cancelBootRetry(): void {
  if (bootRetry !== undefined) clearTimeout(bootRetry);
  bootRetry = undefined;
  bootAttempt = 0;
}

/**
 * O relógio dos silêncios temporários.
 *
 * UM timer para todos, armado sempre para a expiração MAIS PRÓXIMA — e não um
 * por silêncio. Quem tem trinta canais calados não fica com trinta despertadores
 * ao mesmo tempo, e o custo de rearmar é ler um mínimo de uma lista pequena.
 *
 * Sem isto, um "silenciar por uma hora" continuava a mostrar o sino cortado para
 * sempre: o servidor deixa de contar o silêncio na hora certa (as consultas
 * filtram pela data), mas esta cópia em memória só voltaria à verdade no próximo
 * Ready. A UI mentiria exactamente sobre o que a pessoa escolheu ter fim.
 *
 * Vive fora do store pela mesma razão que o socket: é um recurso, não estado.
 */
let muteClock: ReturnType<typeof setTimeout> | undefined;

function cancelMuteClock(): void {
  if (muteClock !== undefined) clearTimeout(muteClock);
  muteClock = undefined;
}

/** Os que ainda valem, agora. Um `until` no passado é o mesmo que não existir. */
function liveMutes(mutes: Record<string, Mute>, now: number): Record<string, Mute> {
  const live: Record<string, Mute> = {};
  for (const [key, mute] of Object.entries(mutes)) {
    if (mute.until === null || Date.parse(mute.until) > now) live[key] = mute;
  }
  return live;
}

function scheduleMuteExpiry(): void {
  cancelMuteClock();
  const now = Date.now();
  let soonest = Infinity;
  for (const mute of Object.values(useStore.getState().mutes)) {
    if (mute.until === null) continue;
    const at = Date.parse(mute.until);
    if (at > now && at < soonest) soonest = at;
  }
  if (soonest === Infinity) return;

  // Um milissegundo depois da hora: acordar EM CIMA dela deixava o `>` do
  // `liveMutes` a dar verdade por um triz e o silêncio a sobreviver ao próprio fim.
  muteClock = setTimeout(
    () => {
      useStore.setState((s) => ({ mutes: liveMutes(s.mutes, Date.now()) }));
      scheduleMuteExpiry();
    },
    soonest - now + 1,
  );
}

export const useStore = create<AppState>()((set, get) => ({
  self: null,
  authStatus: "unknown",
  connectionStatus: "idle",
  connectionDetail: null,
  ready: false,

  servers: {},
  channels: {},
  roles: {},
  members: {},
  users: {},
  readStates: {},
  mutes: {},
  emojis: {},
  voiceStates: {},
  messages: {},
  history: {},
  typing: {},
  relationships: {},

  activeServerId: null,
  activeChannelId: null,
  lastChannelByServer: {},
  jumpTarget: null,
  // Coluna no desktop, gaveta no telemóvel — e uma gaveta não arranca aberta.
  memberListOpen: !matchesMobile(),
  navOpen: false,
  sidebarCollapsed: loadLayoutPrefs().sidebarCollapsed,
  voice: null,
  viewMode: "chat",
  serverSettingsTab: null,
  officeCallMin: true,
  officeCallFull: false,
  searchOpen: false,
  positions: {},
  roomLocks: {},
  spaceSays: {},
  spaceChat: {},
  spacePokes: {},
  spaceBolas: {},
  knocks: [],
  ligacao: null,
  ligacaoFim: null,
  spaceCorrection: {},

  // ── sessão ────────────────────────────────────────────────

  async bootstrap() {
    try {
      const self = await api.users.self();
      cancelBootRetry();
      set({ connectionDetail: null });
      get().signedIn(self);
    } catch (error) {
      // 401 é a ÚNICA resposta que quer dizer "não tens sessão": ninguém entrou
      // ainda, ou o cookie caducou. Caso normal, não é um erro a mostrar.
      if (error instanceof ApiError && error.isUnauthenticated) {
        cancelBootRetry();
        set({ authStatus: "anonymous", self: null, connectionDetail: null });
        return;
      }

      // Tudo o resto — API em baixo, rede fora, 500 — é "ainda não sei", e NÃO
      // é a mesma coisa. Mandar isto para o ecrã de entrada faz o utilizador
      // escrever a password outra vez por causa de um reinício do servidor, com
      // a sessão dele intacta na base de dados e o cookie ainda válido no
      // browser. Fica no ecrã de espera, com o motivo à vista, e volta a tentar:
      // quando a API regressa, a sessão reaparece sozinha e ninguém escreveu
      // nada. É a diferença entre "a tua sessão caiu" e "eu é que não cheguei lá".
      const delay = BOOT_BACKOFF_MS[Math.min(bootAttempt, BOOT_BACKOFF_MS.length - 1)];
      bootAttempt += 1;
      if (bootRetry !== undefined) clearTimeout(bootRetry);
      bootRetry = setTimeout(() => void get().bootstrap(), delay);
      set({ connectionDetail: (error as Error).message });
    }
  },

  signedIn(self) {
    set({ self, authStatus: "authenticated", users: { ...get().users, [self.id]: self } });
    get().connect();
  },

  async signOut() {
    get().disconnect();
    // Sair a meio de uma espera por API: o timer pendente ficaria a bater à
    // porta de uma sessão que acabou de ser deitada fora.
    cancelBootRetry();
    // O despertador dos silêncios é o único timer que sobrevive à sessão se
    // ninguém lhe tocar: dispararia sobre o store de quem entrasse a seguir.
    cancelMuteClock();
    // Sem isto, quem entrasse a seguir nesta aba abria os mesmos canais e nunca
    // via os tópicos: a marca de "já pedi" ficava da sessão anterior.
    threadsRequested.clear();
    await api.auth.logout().catch(() => undefined);
    set({
      self: null,
      authStatus: "anonymous",
      ready: false,
      servers: {},
      channels: {},
      roles: {},
      members: {},
      users: {},
      readStates: {},
      mutes: {},
      emojis: {},
      voiceStates: {},
      messages: {},
      history: {},
      typing: {},
      relationships: {},
      activeServerId: null,
      activeChannelId: null,
      jumpTarget: null,
      voice: null,
      viewMode: "chat",
      serverSettingsTab: null,
      officeCallMin: true,
      officeCallFull: false,
      searchOpen: false,
      positions: {},
      roomLocks: {},
      spaceSays: {},
      spaceChat: {},
      spacePokes: {},
      spaceBolas: {},
      knocks: [],
      ligacao: null,
      ligacaoFim: null,
      spaceCorrection: {},
    });
  },

  connect() {
    if (gateway) return;
    gateway = new GatewayClient({
      onStatus: (status, detail) => set({ connectionStatus: status, connectionDetail: detail ?? null }),
      onReady: (payload) => get().applyReady(payload),
      onEvent: (event) => get().applyEvent(event),
      // Pedido recusado com a ligação de pé. Não mexe no estado da ligação (não é
      // uma queda), mas fica registado: uma recusa que não deixa rasto nenhuma é
      // uma acção que "não faz nada" sem ninguém saber porquê.
      onError: (code, message) => set({ connectionDetail: `${message} (${code})` }),
    });
    gateway.start();
  },

  disconnect() {
    gateway?.stop();
    gateway = undefined;
    set({ ready: false, connectionStatus: "idle" });
  },

  // ── Ready: o estado inicial completo ──────────────────────

  applyReady(payload) {
    const servers = byId(payload.servers);
    const channels = byId(payload.channels);
    const roles = byId(payload.roles);
    const users = byId(payload.users);
    users[payload.user.id] = payload.user;

    const members: Record<string, Member> = {};
    for (const member of payload.members) {
      members[memberKey(member.serverId, member.user.id)] = member;
      users[member.user.id] = member.user;
    }

    const readStates: Record<Snowflake, ReadState> = {};
    for (const state of payload.readStates) readStates[state.channelId] = state;

    // Filtrados outra vez pela data, apesar de o servidor já os mandar vivos: o
    // Ready de uma reconexão pode chegar de uma sessão que estava suspensa há
    // horas, e o relógio local é quem sabe que horas são agora.
    const mutes = liveMutes(
      Object.fromEntries(payload.mutes.map((mute) => [muteKey(mute.scope, mute.targetId), mute])),
      Date.now(),
    );

    const emojis: Record<Snowflake, Emoji> = {};
    for (const emoji of payload.emojis) emojis[emoji.id] = emoji;

    const voiceStates: Record<Snowflake, VoiceState> = {};
    for (const state of payload.voiceStates) voiceStates[state.userId] = state;

    const previous = get();

    // Os tópicos não vêm no Ready, e o `set` abaixo troca o dicionário INTEIRO:
    // sem isto, uma reconexão fazia desaparecer o tópico aberto debaixo do rato
    // e o `activeChannelId` caía para null. Salvam-se os que já cá estavam e
    // cujo canal pai sobreviveu — um tópico de um canal que deixei de ver não
    // volta a entrar. A lista verdadeira é pedida outra vez ao abrir o pai, e é
    // aí que um tópico fechado entretanto por outra pessoa cai daqui.
    for (const channel of Object.values(previous.channels)) {
      if (channel.type === "THREAD" && channel.parentId && channels[channel.parentId]) {
        channels[channel.id] = channel;
      }
    }
    threadsRequested.clear();

    // Um Ready depois de uma reconexão longa substitui as entidades, mas NÃO o
    // que o utilizador tem à frente: perder o canal aberto (e o scroll) por
    // causa de uma queda de rede é o pior sintoma possível de um reconnect.
    const activeChannelId =
      previous.activeChannelId && channels[previous.activeChannelId] ? previous.activeChannelId : null;
    const activeServerId = previous.activeServerId && servers[previous.activeServerId] ? previous.activeServerId : null;

    set({
      self: payload.user,
      authStatus: "authenticated",
      ready: true,
      servers,
      channels,
      roles,
      members,
      users,
      readStates,
      mutes,
      emojis,
      voiceStates,
      activeServerId,
      activeChannelId,
      // O histórico das mensagens sobrevive: as que faltarem chegam por evento
      // (resume) ou pelo scroll. Recarregar tudo à mão seria pior.
    });

    scheduleMuteExpiry();

    // O escritório 2D é a aplicação primária: se não há nada aberto à frente
    // (arranque limpo, sem servidor preservado), entra logo no escritório do
    // primeiro servidor em vez de aterrar na vista @me vazia.
    if (!activeServerId) {
      const first = Object.values(servers).sort((a, b) => idCompare(a.id, b.id))[0];
      if (first) get().selectServer(first.id);
    }

    // As relações não vêm no Ready — pedem-se aqui, uma vez ligados. Falhar não
    // pode partir o arranque: a lista de amigos aparece vazia até um retry.
    void get().loadRelationships();
  },

  async loadRelationships() {
    try {
      const list = await api.users.relationships();
      const relationships: Record<Snowflake, Relationship> = {};
      const users: Record<Snowflake, PublicUser> = {};
      for (const relation of list) {
        relationships[relation.user.id] = relation;
        users[relation.user.id] = relation.user;
      }
      set((s) => ({ relationships, users: { ...s.users, ...users } }));
    } catch {
      // Silencioso de propósito: um erro aqui não deve encher a UI de vermelho;
      // a próxima abertura do painel de amigos volta a tentar.
    }
  },

  // ── deltas ────────────────────────────────────────────────

  applyEvent(event) {
    switch (event.type) {
      // ── mensagens ────────────────────────────────────────
      case "MessageCreate": {
        upsertMessage(set, get, event.message);
        const state = get();
        const focused =
          state.activeChannelId === event.message.channelId &&
          typeof document !== "undefined" &&
          document.visibilityState === "visible";
        // Se o canal está aberto e com foco, marcar lido de imediato — senão
        // o badge acende para uma mensagem que a pessoa está mesmo a ler.
        if (focused) {
          state.markRead(event.message.channelId);
        } else if (event.message.author?.id !== state.self?.id) {
          // Chegou fora da minha vista e não fui eu que a escrevi: toca o "ping".
          // Não apita para as minhas próprias mensagens nem para o canal que
          // estou mesmo a ler — só para o que exige atenção.
          playSound("notify");
        }
        break;
      }

      case "MessageUpdate": {
        patchMessage(set, get, event.channelId, event.messageId, event.message);
        break;
      }

      case "MessageDelete": {
        set((s) => ({
          messages: {
            ...s.messages,
            [event.channelId]: (s.messages[event.channelId] ?? []).filter((m) => m.id !== event.messageId),
          },
        }));
        break;
      }

      case "MessageBulkDelete": {
        const removed = new Set(event.messageIds);
        set((s) => ({
          messages: {
            ...s.messages,
            [event.channelId]: (s.messages[event.channelId] ?? []).filter((m) => !removed.has(m.id)),
          },
        }));
        break;
      }

      case "MessageReact":
      case "MessageUnreact": {
        const mine = event.userId === get().self?.id;
        const add = event.type === "MessageReact";
        patchMessageBy(set, get, event.channelId, event.messageId, (message) => ({
          ...message,
          reactions: applyReaction(message.reactions, event.emoji, add, mine),
        }));
        break;
      }

      case "MessageAck": {
        set((s) => ({
          readStates: {
            ...s.readStates,
            [event.channelId]: {
              channelId: event.channelId,
              lastReadMessageId: event.messageId,
              mentionCount: 0,
            },
          },
        }));
        break;
      }

      // ── canais ───────────────────────────────────────────
      case "ChannelCreate": {
        set((s) => ({ channels: { ...s.channels, [event.channel.id]: event.channel } }));
        break;
      }

      case "ChannelUpdate": {
        set((s) => {
          const existing = s.channels[event.channelId];
          if (!existing) return {};
          return { channels: { ...s.channels, [event.channelId]: { ...existing, ...event.channel } } };
        });
        break;
      }

      case "ChannelDelete": {
        set((s) => {
          const channels = { ...s.channels };
          delete channels[event.channelId];
          const messages = { ...s.messages };
          delete messages[event.channelId];
          return {
            channels,
            messages,
            activeChannelId: s.activeChannelId === event.channelId ? null : s.activeChannelId,
          };
        });
        break;
      }

      case "ChannelStartTyping": {
        if (event.userId === get().self?.id) break;
        set((s) => ({
          typing: {
            ...s.typing,
            [event.channelId]: {
              ...(s.typing[event.channelId] ?? {}),
              [event.userId]: Date.now() + TYPING_TTL_MS,
            },
          },
        }));
        break;
      }

      case "ChannelStopTyping": {
        set((s) => {
          const channel = { ...(s.typing[event.channelId] ?? {}) };
          delete channel[event.userId];
          return { typing: { ...s.typing, [event.channelId]: channel } };
        });
        break;
      }

      // ── servidores ───────────────────────────────────────
      case "ServerCreate": {
        set((s) => ({
          servers: { ...s.servers, [event.server.id]: event.server },
          channels: { ...s.channels, ...byId(event.channels) },
          roles: { ...s.roles, ...byId(event.roles) },
        }));
        break;
      }

      case "ServerUpdate": {
        set((s) => {
          const existing = s.servers[event.serverId];
          if (!existing) return {};
          return { servers: { ...s.servers, [event.serverId]: { ...existing, ...event.server } } };
        });
        break;
      }

      case "ServerDelete": {
        set((s) => {
          const servers = { ...s.servers };
          delete servers[event.serverId];
          const channels = Object.fromEntries(
            Object.entries(s.channels).filter(([, c]) => c.serverId !== event.serverId),
          );
          return {
            servers,
            channels,
            activeServerId: s.activeServerId === event.serverId ? null : s.activeServerId,
            activeChannelId: s.activeChannelId && !channels[s.activeChannelId] ? null : s.activeChannelId,
          };
        });
        break;
      }

      case "ServerMemberJoin": {
        set((s) => {
          const key = memberKey(event.member.serverId, event.member.user.id);
          // Só CONTA quem ainda não estava cá dentro. Este evento não chega só
          // quando alguém entra no servidor: é também a resposta a cada
          // `requestMembers`, que se pede de novo a cada troca de servidor, a
          // cada reconexão e agora sempre que se abre a lista do escritório —
          // e sem esta guarda o `memberCount` do cabeçalho subia a cada uma
          // dessas vezes até dizer o triplo das pessoas que lá estão.
          const known = key in s.members;
          return {
            members: { ...s.members, [key]: event.member },
            users: { ...s.users, [event.member.user.id]: event.member.user },
            servers: known ? s.servers : bumpMemberCount(s.servers, event.member.serverId, +1),
          };
        });
        break;
      }

      case "ServerMemberUpdate": {
        set((s) => {
          const key = memberKey(event.serverId, event.userId);
          const existing = s.members[key];
          if (!existing) return {};
          return { members: { ...s.members, [key]: { ...existing, ...event.member } } };
        });
        break;
      }

      case "ServerMemberLeave": {
        set((s) => {
          const members = { ...s.members };
          delete members[memberKey(event.serverId, event.userId)];
          return { members, servers: bumpMemberCount(s.servers, event.serverId, -1) };
        });
        break;
      }

      case "ServerRoleCreate": {
        set((s) => ({ roles: { ...s.roles, [event.role.id]: event.role } }));
        break;
      }

      case "ServerRoleUpdate": {
        set((s) => {
          const existing = s.roles[event.roleId];
          if (!existing) return {};
          return { roles: { ...s.roles, [event.roleId]: { ...existing, ...event.role } } };
        });
        break;
      }

      case "ServerRoleDelete": {
        set((s) => {
          const roles = { ...s.roles };
          delete roles[event.roleId];
          return { roles };
        });
        break;
      }

      // ── utilizadores / presença ──────────────────────────
      case "UserUpdate": {
        set((s) => {
          const existing = s.users[event.userId];
          if (!existing) return {};
          const merged = { ...existing, ...event.user };
          return {
            users: { ...s.users, [event.userId]: merged },
            self: s.self?.id === event.userId ? { ...s.self, ...event.user } : s.self,
          };
        });
        break;
      }

      case "PresenceUpdate": {
        set((s) => {
          // `statusText` AUSENTE não é `statusText: null`. Ausente quer dizer
          // "não falei do texto"; `null` quer dizer "apaga-o". O servidor mandava
          // sempre `?? null` e cada mudança de estado — cada ligação, cada
          // desligar — limpava o recado de toda a gente aqui dentro.
          const patch =
            event.statusText === undefined
              ? { status: event.status }
              : { status: event.status, statusText: event.statusText };

          // Também o próprio: o estado escolhido no telemóvel chega aqui pelo
          // mesmo evento, e `self` e `users[eu]` têm de continuar a concordar
          // (ver `setPresence`) — senão o rodapé e o avatar dizem coisas
          // diferentes sobre a mesma pessoa.
          const self = s.self?.id === event.userId ? { ...s.self, ...patch } : s.self;
          const existing = s.users[event.userId];
          if (!existing) return self === s.self ? {} : { self };
          return { self, users: { ...s.users, [event.userId]: { ...existing, ...patch } } };
        });
        break;
      }

      case "RelationshipUpdate": {
        set((s) => {
          // O evento não traz `createdAt` (só user+kind); guardamos o que já
          // tínhamos, e para uma relação nova carimbamos agora — só serve para
          // ordenar os pedidos por data, não é um dado do servidor.
          const previous = s.relationships[event.user.id];
          const createdAt = previous?.createdAt ?? new Date().toISOString();
          return {
            users: { ...s.users, [event.user.id]: event.user },
            relationships: {
              ...s.relationships,
              [event.user.id]: { user: event.user, kind: event.kind, createdAt },
            },
          };
        });
        break;
      }

      case "RelationshipDelete": {
        set((s) => {
          if (!s.relationships[event.userId]) return {};
          const relationships = { ...s.relationships };
          delete relationships[event.userId];
          return { relationships };
        });
        break;
      }

      // ── silêncios ────────────────────────────────────────
      // Só chegam ao próprio: é preferência pessoal. Existem para o telemóvel e
      // o portátil não discordarem sobre um canal calado num deles.
      case "MuteUpdate": {
        set((s) => ({ mutes: { ...s.mutes, [muteKey(event.mute.scope, event.mute.targetId)]: event.mute } }));
        scheduleMuteExpiry();
        break;
      }

      case "MuteDelete": {
        set((s) => {
          const key = muteKey(event.scope, event.targetId);
          if (!s.mutes[key]) return {};
          const mutes = { ...s.mutes };
          delete mutes[key];
          return { mutes };
        });
        scheduleMuteExpiry();
        break;
      }

      // ── emojis personalizados ────────────────────────────
      // `Create` e `Update` são a mesma escrita: o evento traz o emoji inteiro,
      // e um `Update` de um emoji que este cliente ainda não conhecia (entrou no
      // servidor entre o Ready e agora) tem de o acrescentar, não de o ignorar.
      case "ServerEmojiCreate":
      case "ServerEmojiUpdate": {
        set((s) => ({ emojis: { ...s.emojis, [event.emoji.id]: event.emoji } }));
        break;
      }

      case "ServerEmojiDelete": {
        set((s) => {
          if (!s.emojis[event.emojiId]) return {};
          const emojis = { ...s.emojis };
          delete emojis[event.emojiId];
          return { emojis };
        });
        // As mensagens antigas que o citam ficam como estão. Varrer o histórico
        // para apagar o token seria reescrever o que as pessoas escreveram por
        // causa de uma imagem que deixou de existir.
        break;
      }

      // ── voz / ecrã ───────────────────────────────────────
      case "VoiceStateUpdate": {
        set((s) => ({ voiceStates: { ...s.voiceStates, [event.state.userId]: event.state } }));
        break;
      }

      case "VoiceStateLeave": {
        set((s) => {
          const voiceStates = { ...s.voiceStates };
          delete voiceStates[event.userId];
          return { voiceStates };
        });
        break;
      }

      case "ScreenShareStart":
      case "ScreenShareStop": {
        const sharing = event.type === "ScreenShareStart";
        set((s) => {
          const existing = s.voiceStates[event.userId];
          if (!existing) return {};
          return {
            voiceStates: { ...s.voiceStates, [event.userId]: { ...existing, screenSharing: sharing } },
          };
        });
        break;
      }

      // ── escritório 2D ────────────────────────────────────
      // Só os REMOTOS entram no mapa de posições. O próprio jogador é desenhado
      // a partir do input local (a fonte da sua posição), nunca a partir do que
      // volta pela rede — reconciliar-se a si próprio dava um "elástico" visível.
      case "SpaceState": {
        const selfId = get().self?.id;
        const occupants: Record<Snowflake, SpaceOccupant> = {};
        for (const o of event.occupants) {
          if (o.userId !== selfId) occupants[o.userId] = o;
        }
        set((s) => ({
          positions: { ...s.positions, [event.spaceId]: occupants },
          // As trancas vêm no mesmo snapshot: quem chega tem de ver as portas
          // fechadas antes de dar o primeiro passo, senão andava contra uma
          // porta invisível e só o servidor o travava.
          roomLocks: { ...s.roomLocks, [event.spaceId]: event.locks },
          // O histórico do servidor manda; um servidor antigo não o traz e fica o que havia.
          spaceChat: event.chat ? { ...s.spaceChat, [event.spaceId]: event.chat } : s.spaceChat,
        }));
        break;
      }

      case "RoomLockUpdate": {
        set((s) => {
          const space = { ...(s.roomLocks[event.spaceId] ?? {}) };
          if (event.by === null) delete space[event.roomId];
          else space[event.roomId] = event.by;
          return { roomLocks: { ...s.roomLocks, [event.spaceId]: space } };
        });
        break;
      }

      case "RoomKnock": {
        // A batida vai para o espaço inteiro (o tópico do Redis é por espaço),
        // mas só interessa a duas pessoas: quem bateu e quem está lá dentro.
        // Filtrar aqui em vez de na UI evita acumular notificações que ninguém
        // vai ver — e o `prune` só tem de correr enquanto houver alguma.
        const state = get();
        const mine = event.userId === state.self?.id;
        const inside = state.viewMode === "office" && state.activeServerId === event.spaceId;
        if (!mine && !inside) break;
        set((s) => ({
          knocks: [
            // Bater outra vez à mesma porta renova o pedido em vez de o duplicar.
            ...s.knocks.filter(
              (k) => !(k.spaceId === event.spaceId && k.roomId === event.roomId && k.userId === event.userId),
            ),
            { spaceId: event.spaceId, roomId: event.roomId, userId: event.userId, at: Date.now(), accept: null },
          ],
        }));
        if (!mine) playSound("notify");
        break;
      }

      case "RoomKnockAnswer": {
        set((s) => {
          const idx = s.knocks.findIndex(
            (k) => k.spaceId === event.spaceId && k.roomId === event.roomId && k.userId === event.userId,
          );
          if (idx < 0) return {};
          const knocks = [...s.knocks];
          // `at` volta a agora: a resposta fica visível uns segundos e sai, em
          // vez de herdar o tempo já gasto do pedido e desaparecer num piscar.
          knocks[idx] = { ...knocks[idx]!, at: Date.now(), accept: event.accept };
          return { knocks };
        });
        break;
      }

      case "SpaceSay": {
        // Chega também o nosso próprio eco: o balão local nasce da rede como
        // qualquer outro, e assim há UM só caminho de desenho para todos.
        set((s) => ({
          spaceSays: {
            ...s.spaceSays,
            [event.spaceId]: {
              ...(s.spaceSays[event.spaceId] ?? {}),
              [event.userId]: { text: event.text, at: Date.now() },
            },
          },
          spaceChat: {
            ...s.spaceChat,
            [event.spaceId]: [
              ...(s.spaceChat[event.spaceId] ?? []),
              { userId: event.userId, text: event.text, at: event.at ?? Date.now() },
            ].slice(-SPACE_CHAT_HISTORY),
          },
        }));
        break;
      }

      // ── ligações privadas ─────────────────────────────────
      // Chegam só às duas pontas; mesmo assim confirma-se que é connosco.
      case "LigacaoATocar": {
        const selfId = get().self?.id;
        if (!selfId || (event.de !== selfId && event.para !== selfId)) break;
        const liguei = event.de === selfId;
        set({
          ligacao: {
            id: event.ligacaoId,
            spaceId: event.spaceId,
            outro: liguei ? event.para : event.de,
            liguei,
            estado: "a_tocar",
            desde: Date.now(),
          },
          ligacaoFim: null,
        });
        break;
      }

      case "LigacaoAtendida": {
        const atual = get().ligacao;
        if (!atual || atual.id !== event.ligacaoId) break;
        set({ ligacao: { ...atual, estado: "em_curso", desde: Date.now() } });
        break;
      }

      case "LigacaoTerminada": {
        const selfId = get().self?.id;
        if (!selfId || (event.de !== selfId && event.para !== selfId)) break;
        const atual = get().ligacao;
        // O fim de OUTRA ligação não mexe na minha. Um "ocupado" chega sem nunca
        // ter tocado (não há `ligacao` local) e mostra-se na mesma.
        if (atual && atual.id !== event.ligacaoId) break;
        const liguei = event.de === selfId;
        set({
          ligacao: null,
          ligacaoFim: { outro: liguei ? event.para : event.de, motivo: event.motivo, liguei, at: Date.now() },
        });
        break;
      }

      case "SpacePoke": {
        // O toque vai para o espaço todo e é AQUI que se filtra: o barulhão é
        // só para quem foi chamado. Toda a gente guarda o registo — o abanão do
        // avatar tem de se ver em qualquer ecrã aberto no mapa, senão quem chama
        // não tem confirmação nenhuma de que a chamada saiu.
        if (event.targetId === get().self?.id) playSound("poke");
        set((s) => ({
          spacePokes: {
            ...s.spacePokes,
            [event.spaceId]: {
              ...(s.spacePokes[event.spaceId] ?? {}),
              [event.targetId]: { by: event.userId, at: Date.now() },
            },
          },
        }));
        break;
      }

      case "SpaceBolaDeNeve": {
        contadorDeBolas += 1;
        const bola: BolaNoAr = {
          id: `${event.userId}-${Date.now()}-${contadorDeBolas}`,
          userId: event.userId,
          deX: event.deX,
          deY: event.deY,
          paraX: event.paraX,
          paraY: event.paraY,
          targetId: event.targetId,
        };
        set((s) => ({
          spaceBolas: {
            ...s.spaceBolas,
            [event.spaceId]: [...(s.spaceBolas[event.spaceId] ?? []), bola].slice(-16),
          },
        }));
        break;
      }

      case "PositionUpdate": {
        // A nossa própria posição volta pela rede a CADA passo — o servidor faz
        // o eco ao espaço todo, nós incluídos. Mas só a puxamos de volta quando
        // vem marcada `correction`: aí o servidor RECUSOU o passo (uma porta
        // trancada no instante em que a atravessávamos) e manda-nos para onde
        // estávamos. O eco de sucesso (sem a flag) IGNORA-SE: somos donos do
        // nosso avatar local e já lá estamos — reconciliar-nos com um eco
        // atrasado de RTT é exactamente o "elástico" que se quer evitar (invisível
        // em localhost, saltava à vista pelo túnel). A correcção entra numa gaveta
        // à parte que o Phaser consome uma vez, nunca em `positions`.
        if (event.userId === get().self?.id) {
          if (event.correction !== true) break;
          set((s) => ({
            spaceCorrection: {
              ...s.spaceCorrection,
              [event.spaceId]: { userId: event.userId, x: event.x, y: event.y, facing: event.facing },
            },
          }));
          break;
        }
        set((s) => ({
          positions: {
            ...s.positions,
            [event.spaceId]: {
              ...(s.positions[event.spaceId] ?? {}),
              [event.userId]: {
                userId: event.userId,
                x: event.x,
                y: event.y,
                facing: event.facing,
              },
            },
          },
        }));
        break;
      }

      case "SpaceLeave": {
        set((s) => {
          const space = s.positions[event.spaceId];
          // Quem se foi embora não pode continuar a bater à porta: o pedido sai
          // com ele, senão ficava a piscar até ao TTL para uma pessoa que já não
          // está lá para entrar.
          const knocks = s.knocks.filter((k) => !(k.spaceId === event.spaceId && k.userId === event.userId));
          const dropped = knocks.length !== s.knocks.length ? { knocks } : {};
          if (!space?.[event.userId]) return dropped;
          const next = { ...space };
          delete next[event.userId];
          return { ...dropped, positions: { ...s.positions, [event.spaceId]: next } };
        });
        break;
      }
    }
  },

  // ── navegação ─────────────────────────────────────────────

  selectServer(serverId) {
    const state = get();
    // O @me (mensagens directas) não tem escritório: aí a vista é sempre o chat.
    if (serverId === null) {
      set({ activeServerId: null, activeChannelId: state.lastChannelByServer["@me"] ?? null, viewMode: "chat" });
      return;
    }
    const remembered = state.lastChannelByServer[serverId];
    const fallback = Object.values(state.channels)
      .filter((c) => c.serverId === serverId && (c.type === "TEXT" || c.type === "ANNOUNCEMENT"))
      .sort((a, b) => a.position - b.position || idCompare(a.id, b.id))[0];
    const channelId = remembered && state.channels[remembered] ? remembered : (fallback?.id ?? null);

    // Entrar num servidor é entrar no ESCRITÓRIO 2D — a vista primária. O chat do
    // GreenVoice é a vista secundária, aberta pelo toggle da barra de canais.
    // Também no telemóvel: o mundo anda com o joystick de toque (`OfficeTouchControls`).
    const entry = "office";
    set({ activeServerId: serverId, activeChannelId: channelId, viewMode: entry });
    if (channelId) {
      // Carregamos o canal de texto (para o chat estar pronto ao alternar) mas
      // sem sair do escritório: o selectChannel força "chat", reafirmamos office.
      get().selectChannel(channelId);
      set({ viewMode: entry });
    }
  },

  selectChannel(channelId) {
    const channel = get().channels[channelId];
    if (!channel) return;
    if (channel.type === "CATEGORY") return;

    set((s) => ({
      activeChannelId: channelId,
      activeServerId: channel.serverId,
      lastChannelByServer: { ...s.lastChannelByServer, [channel.serverId ?? "@me"]: channelId },
      // Escolher um canal é pedir o chat: sai do escritório se lá estava.
      viewMode: "chat",
      serverSettingsTab: null,
    }));

    if (
      channel.type === "TEXT" ||
      channel.type === "ANNOUNCEMENT" ||
      channel.type === "DM" ||
      channel.type === "GROUP_DM" ||
      // Um tópico é um canal de texto como os outros. Sem esta linha abria
      // sempre vazio: não há mais nada nesta função a pedir histórico.
      channel.type === "THREAD"
    ) {
      const history = get().history[channelId] ?? EMPTY_HISTORY;
      if (!history.loaded && !history.loading) void get().loadMessages(channelId);
      else get().markRead(channelId);
    }

    // Os tópicos do canal, uma vez por ligação. Só onde eles podem existir: um
    // tópico não tem tópicos dentro, e um DM também não.
    if ((channel.type === "TEXT" || channel.type === "ANNOUNCEMENT") && !threadsRequested.has(channelId)) {
      threadsRequested.add(channelId);
      // Sem esperar e a engolir a falha: a lista de tópicos é acessória e não
      // pode atrasar nem partir a abertura do canal. Se falhar tira-se a marca,
      // para a próxima entrada voltar a tentar em vez de ficar vazia para sempre.
      void get()
        .loadThreads(channelId)
        .catch(() => threadsRequested.delete(channelId));
    }
  },

  async openDirectMessage(userId) {
    // Não há "criar DM" e "abrir DM" separados de propósito: o servidor devolve
    // a conversa que já existe quando o destinatário é um só (`findDm`). Se
    // fosse esta camada a decidir, abrir o perfil de alguém duas vezes em dois
    // separadores criava duas caixas para a mesma pessoa.
    // `recipientIds` de um DM traz os dois lados (eu e ele), por isso basta
    // procurar o outro. Um GROUP_DM com a mesma pessoa lá dentro não serve:
    // "enviar mensagem" é uma conversa a dois.
    const existing = Object.values(get().channels).find((c) => c.type === "DM" && c.recipientIds.includes(userId));
    if (existing) {
      get().selectChannel(existing.id);
      get().closeDrawers();
      return;
    }

    const channel = await api.users.createChannel({ recipientIds: [userId] });
    set((s) => ({ channels: { ...s.channels, [channel.id]: channel } }));
    get().selectChannel(channel.id);
    get().closeDrawers();
  },

  toggleMemberList() {
    // Abrir uma gaveta fecha a outra: num ecrã de 390px as duas ao mesmo tempo
    // não deixavam ver nada do que está por baixo, que é a conversa.
    set((s) => ({ memberListOpen: !s.memberListOpen, navOpen: false }));
  },

  toggleNav() {
    set((s) => ({ navOpen: !s.navOpen, memberListOpen: s.navOpen ? s.memberListOpen : false }));
  },

  closeDrawers() {
    set({ navOpen: false, memberListOpen: false });
  },

  toggleSidebar() {
    const sidebarCollapsed = !get().sidebarCollapsed;
    set({ sidebarCollapsed });
    saveLayoutPrefs({ sidebarCollapsed });
  },

  setOfficeCallMin(min) {
    // Voltar ao cartão de canto é voltar a ver o mundo — portanto a bandeira de
    // "está a tapar o mundo" cai JUNTO, na mesma escrita. Deixá-la para o efeito
    // do escritório dava um frame com a barra de cima ainda a dizer "Escritório"
    // sobre um mundo já visível.
    set({ officeCallMin: min, ...(min ? { officeCallFull: false } : {}) });
  },

  setOfficeCallFull(full) {
    set({ officeCallFull: full });
  },

  toggleSearch() {
    // Abrir a procura fecha a lista de membros, porque ocupam a MESMA coluna —
    // é a mesma troca que o Discord faz, e é ela que dá largura aos resultados
    // sem espremer a conversa.
    set((s) => ({ searchOpen: !s.searchOpen }));
  },

  closeSearch() {
    set({ searchOpen: false });
  },

  setViewMode(mode) {
    set({ viewMode: mode });
  },

  openServerSettings(tab) {
    set({ serverSettingsTab: tab });
  },

  limparFimDeLigacao() {
    set({ ligacaoFim: null });
  },

  // ── mensagens ─────────────────────────────────────────────

  async loadMessages(channelId, options) {
    const current = get().history[channelId] ?? EMPTY_HISTORY;
    if (current.loading) return;
    if (options?.before && current.atStart) return;
    if (options?.after && current.atEnd) return;

    // Sem direcção nenhuma há dois pedidos diferentes com a mesma cara: o
    // primeiro carregamento do canal, e o "voltar ao presente" de quem está a
    // ver uma janela antiga. O segundo tem de DEITAR FORA a janela — juntá-la ao
    // fim do canal punha uma conversa de Março colada à de hoje.
    const substitui = Boolean(options?.around) || (!options?.before && !options?.after && !current.atEnd);

    setHistory(set, channelId, { ...current, loading: true, error: null });

    try {
      const batch = await api.messages.list(channelId, {
        limit: 50,
        ...(options?.before ? { before: options.before } : {}),
        ...(options?.after ? { after: options.after } : {}),
        ...(options?.around ? { around: options.around } : {}),
      });
      // A API devolve do mais recente para o mais antigo; guardamos ao
      // contrário, que é a ordem em que se lê e se desenha.
      const ordered = [...batch].reverse();

      set((s) => {
        const existing = substitui ? [] : (s.messages[channelId] ?? []);
        const merged = mergeMessages(existing, ordered);
        return { messages: { ...s.messages, [channelId]: merged } };
      });

      // Chegar ao fim é uma igualdade, não uma comparação: a última mensagem
      // carregada é MESMO a última do canal. Comparar ids com `>` obrigava a
      // confiar no comprimento do snowflake, e aqui não é preciso.
      const newest = ordered[ordered.length - 1]?.id ?? null;
      const lastId = get().channels[channelId]?.lastMessageId ?? null;
      const noFim = lastId === null || newest === lastId;

      setHistory(set, channelId, {
        loading: false,
        loaded: true,
        // Uma janela `around` traz metade de cada lado e o cliente não vê o
        // corte: um lote curto tanto pode ser o início do canal como o fim.
        // Fica `false` e é a primeira página para trás que descobre a verdade.
        atStart: options?.around ? false : options?.after ? current.atStart : batch.length < 50,
        atEnd: options?.around || options?.after ? noFim : true,
        error: null,
      });

      if (!options?.before && !options?.around) get().markRead(channelId);
    } catch (error) {
      setHistory(set, channelId, {
        ...current,
        loading: false,
        loaded: true,
        error: (error as Error).message,
      });
    }
  },

  async jumpToMessage(channelId, messageId) {
    const state = get();
    // Já está carregada (a procura devolveu algo que está mesmo ali em cima, ou
    // é a mensagem citada por uma resposta): não se pede nada, só se aponta.
    const presente = (state.messages[channelId] ?? []).some((m) => m.id === messageId);

    if (!presente) {
      // Antes do `selectChannel`, senão ele dispara o carregamento normal do
      // canal e o `loading` engolia este pedido em silêncio.
      await state.loadMessages(channelId, { around: messageId });
    }
    if (state.activeChannelId !== channelId) get().selectChannel(channelId);
    set({ viewMode: "chat", jumpTarget: messageId });
  },

  clearJumpTarget() {
    if (get().jumpTarget !== null) set({ jumpTarget: null });
  },

  async sendMessage(channelId, content, options) {
    const trimmed = content.trim();
    const attachmentIds = options?.attachmentIds ?? [];
    if (!trimmed && attachmentIds.length === 0) return;
    gateway?.endTyping(channelId);

    // Sem optimistic-UI com id temporário de propósito: a resposta do POST já
    // traz a mensagem final e o upsert por id torna o eco do gateway inofensivo.
    // Um id falso obrigaria a reconciliar — e a reconciliação é onde nascem as
    // mensagens duplicadas que toda a gente já viu noutros clientes.
    const message = await api.messages.send(channelId, {
      content: trimmed,
      ...(options?.replyToId ? { replyToId: options.replyToId } : {}),
      ...(attachmentIds.length ? { attachmentIds } : {}),
    });

    // A escrever com uma janela antiga aberta (veio de um resultado da procura):
    // o `upsertMessage` recusa acrescentar ao fim de uma lista que não chega ao
    // presente, e a pessoa escrevia sem ver nada aparecer. Volta-se ao presente,
    // que é onde a mensagem acabada de enviar está.
    if (!(get().history[channelId] ?? EMPTY_HISTORY).atEnd) await get().loadMessages(channelId);
    else upsertMessage(set, get, message);
    get().markRead(channelId);
  },

  async editMessage(channelId, messageId, content) {
    const message = await api.messages.edit(channelId, messageId, content.trim());
    upsertMessage(set, get, message);
  },

  async deleteMessage(channelId, messageId) {
    await api.messages.remove(channelId, messageId);
    set((s) => ({
      messages: {
        ...s.messages,
        [channelId]: (s.messages[channelId] ?? []).filter((m) => m.id !== messageId),
      },
    }));
  },

  async toggleReaction(channelId, messageId, emoji) {
    const message = (get().messages[channelId] ?? []).find((m) => m.id === messageId);
    const mine = message?.reactions.some((r) => r.emoji === emoji && r.me) ?? false;
    if (mine) await api.messages.unreact(channelId, messageId, emoji);
    else await api.messages.react(channelId, messageId, emoji);
    // O estado real chega pelo evento; aqui só se antecipa o feedback visual.
    patchMessageBy(set, get, channelId, messageId, (m) => ({
      ...m,
      reactions: applyReaction(m.reactions, emoji, !mine, true),
    }));
  },

  markRead(channelId) {
    const state = get();
    const messages = state.messages[channelId] ?? [];
    const last = messages[messages.length - 1] ?? null;
    const lastId = last?.id ?? state.channels[channelId]?.lastMessageId ?? null;
    if (!lastId) return;

    const readState = state.readStates[channelId];
    if (readState && !idGreater(lastId, readState.lastReadMessageId) && readState.mentionCount === 0) {
      return;
    }

    set((s) => ({
      readStates: {
        ...s.readStates,
        [channelId]: { channelId, lastReadMessageId: lastId, mentionCount: 0 },
      },
    }));
    void api.messages.ack(channelId, lastId).catch(() => undefined);
  },

  /**
   * Escreve o silêncio e guarda a resposta — sem antecipar nada.
   *
   * A optimista aqui era pior do que a espera que evita: o sino tem de mostrar
   * ATÉ QUANDO, e essa data só existe depois de o servidor a carimbar. Adivinhá-la
   * daqui era escrever no ecrã um "até às 15:04" que podia não ser o que ficou
   * gravado. O evento do gateway chega a seguir com o mesmo conteúdo e reescreve
   * a mesma chave — daí ser inofensivo aplicar os dois.
   */
  async setMute(scope, targetId, minutes) {
    const mute =
      scope === "CHANNEL"
        ? await api.mutes.muteChannel(targetId, minutes)
        : await api.mutes.muteServer(targetId, minutes);
    set((s) => ({ mutes: { ...s.mutes, [muteKey(scope, targetId)]: mute } }));
    scheduleMuteExpiry();
  },

  async clearMute(scope, targetId) {
    // Tirado do estado ANTES da chamada, ao contrário do `setMute`: aqui não há
    // data nenhuma para esperar, e o sino tem de responder ao dedo. Se a chamada
    // falhar, o próximo Ready traz o silêncio de volta — o pior caso é um sino
    // que volta atrás, não uma notificação perdida.
    set((s) => {
      const mutes = { ...s.mutes };
      delete mutes[muteKey(scope, targetId)];
      return { mutes };
    });
    scheduleMuteExpiry();
    if (scope === "CHANNEL") await api.mutes.unmuteChannel(targetId);
    else await api.mutes.unmuteServer(targetId);
  },

  // Nenhuma das três é optimista: a resposta traz o URL definitivo do ficheiro,
  // e sem ele não havia nada para desenhar. O evento do gateway chega a seguir
  // com o mesmo conteúdo e reescreve a mesma chave — inofensivo.
  /**
   * Recarrega a lista de UM servidor. O Ready já traz tudo, mas um servidor onde
   * se entrou a meio da sessão não passou por lá — e é o painel de definições
   * que dá por isso primeiro.
   *
   * Substitui a fatia deste servidor em vez de a fundir: sem o `delete`, um
   * emoji apagado por outra pessoa enquanto a janela estava fechada ficava aqui
   * para sempre, porque nada o vinha tirar.
   */
  async loadEmojis(serverId) {
    const list = await api.emojis.list(serverId);
    set((s) => {
      const emojis = { ...s.emojis };
      for (const [id, emoji] of Object.entries(emojis)) {
        if (emoji.serverId === serverId) delete emojis[id];
      }
      for (const emoji of list) emojis[emoji.id] = emoji;
      return { emojis };
    });
  },

  async createEmoji(serverId, name, fileId) {
    const emoji = await api.emojis.create(serverId, { name, fileId });
    set((s) => ({ emojis: { ...s.emojis, [emoji.id]: emoji } }));
    return emoji;
  },

  async renameEmoji(serverId, emojiId, name) {
    const emoji = await api.emojis.rename(serverId, emojiId, name);
    set((s) => ({ emojis: { ...s.emojis, [emoji.id]: emoji } }));
    return emoji;
  },

  async deleteEmoji(serverId, emojiId) {
    await api.emojis.remove(serverId, emojiId);
    set((s) => {
      if (!s.emojis[emojiId]) return {};
      const emojis = { ...s.emojis };
      delete emojis[emojiId];
      return { emojis };
    });
  },

  /**
   * Os tópicos de um canal. Substitui a fatia deste canal em vez de a fundir,
   * pelo mesmo motivo do `loadEmojis`: um tópico fechado por outra pessoa
   * enquanto a janela estava noutro sítio não tem quem o venha tirar daqui.
   */
  async loadThreads(channelId) {
    const list = await api.threads.list(channelId);
    set((s) => {
      const channels = { ...s.channels };
      for (const [id, channel] of Object.entries(channels)) {
        if (channel.type === "THREAD" && channel.parentId === channelId) delete channels[id];
      }
      for (const thread of list) channels[thread.id] = thread;
      return { channels };
    });
  },

  async createThread(channelId, name, messageId) {
    const thread = await api.threads.create(channelId, {
      name,
      ...(messageId ? { messageId } : {}),
    });
    // Entra já, sem esperar pelo `ChannelCreate` do gateway: quem abre um tópico
    // é levado para dentro dele a seguir, e entrar num canal que o store ainda
    // não tem dava um ecrã vazio até o socket chegar.
    set((s) => ({ channels: { ...s.channels, [thread.id]: thread } }));
    return thread;
  },

  notifyTyping(channelId) {
    gateway?.beginTyping(channelId);
  },

  pruneTyping() {
    const now = Date.now();
    const state = get();
    let changed = false;
    const typing: AppState["typing"] = {};

    for (const [channelId, users] of Object.entries(state.typing)) {
      const alive: Record<Snowflake, number> = {};
      for (const [userId, expiry] of Object.entries(users)) {
        if (expiry > now) alive[userId] = expiry;
        else changed = true;
      }
      typing[channelId] = alive;
    }
    if (changed) set({ typing });
  },

  pruneKnocks() {
    const now = Date.now();
    const knocks = get().knocks;
    const alive = knocks.filter((k) => now - k.at < (k.accept === null ? KNOCK_TTL_MS : KNOCK_ANSWERED_TTL_MS));
    // Só escreve se cortou alguma: um `set` por segundo com o mesmo conteúdo
    // re-renderizava o escritório inteiro sem nada ter mudado.
    if (alive.length !== knocks.length) set({ knocks: alive });
  },

  setPresence(status, statusText) {
    gateway?.updatePresence(status, statusText);
    set((s) => {
      if (!s.self) return {};
      const self = { ...s.self, status, statusText: statusText ?? s.self.statusText };
      // O `users` também me tem lá dentro (sou membro dos servidores onde entro)
      // e é DELE que a lista de membros e o escritório lêem o estado de toda a
      // gente — o meu incluído. Escrever só no `self` deixava-me a mim "online"
      // no sítio exacto onde os outros já me viam ausente, até o servidor
      // devolver o eco.
      const existing = s.users[self.id];
      if (!existing) return { self };
      return {
        self,
        users: { ...s.users, [self.id]: { ...existing, status: self.status, statusText: self.statusText } },
      };
    });
  },

  setVoice(patch) {
    if (patch === null) {
      set({ voice: null });
      return;
    }
    set((s) => (s.voice ? { voice: { ...s.voice, ...patch } } : {}));
  },
}));

/** Acesso ao gateway para quem precisa de falar com ele fora do store (voz). */
export function gatewayClient(): GatewayClient | undefined {
  return gateway;
}

// ── auxiliares ───────────────────────────────────────────────

type Setter = (partial: Partial<AppState> | ((state: AppState) => Partial<AppState>)) => void;
type Getter = () => AppState;

function byId<T extends { id: string }>(items: readonly T[]): Record<string, T> {
  const map: Record<string, T> = {};
  for (const item of items) map[item.id] = item;
  return map;
}

function setHistory(set: Setter, channelId: Snowflake, history: HistoryState): void {
  set((s) => ({ history: { ...s.history, [channelId]: history } }));
}

function bumpMemberCount(
  servers: Record<Snowflake, Server>,
  serverId: Snowflake,
  delta: number,
): Record<Snowflake, Server> {
  const server = servers[serverId];
  if (!server) return servers;
  return {
    ...servers,
    [serverId]: { ...server, memberCount: Math.max(0, server.memberCount + delta) },
  };
}

/**
 * Insere mantendo a ordem por id e sem duplicar. O caso comum (mensagem nova,
 * que é sempre a mais recente) sai no primeiro `if` sem percorrer nada.
 */
function upsertMessage(set: Setter, _get: Getter, message: Message): void {
  set((s) => {
    const users = message.author ? { ...s.users, [message.author.id]: message.author } : s.users;
    const list = s.messages[message.channelId] ?? [];
    const last = list[list.length - 1];
    const index = list.findIndex((m) => m.id === message.id);

    // Com uma janela antiga aberta (saltou-se para um resultado da procura) a
    // lista NÃO chega ao presente: entre ela e esta mensagem está tudo o que não
    // foi carregado, e colá-la ao fim inventava uma conversa que nunca existiu.
    // A alteração de uma mensagem que já lá está continua a valer — essa é a
    // mesma mensagem, não um pedaço novo.
    const separada = index < 0 && !(s.history[message.channelId] ?? EMPTY_HISTORY).atEnd;

    let next = list;
    if (!separada) {
      if (index >= 0) {
        next = [...list];
        next[index] = message;
      } else if (!last || idGreater(message.id, last.id)) {
        next = [...list, message];
      } else {
        next = [...list, message].sort((a, b) => idCompare(a.id, b.id));
      }
    }

    // Só avança, nunca recua: editar uma mensagem antiga passava por aqui e
    // punha o `lastMessageId` do canal para trás — e é contra ele que se decide
    // se a lista chega ao presente.
    const channel = s.channels[message.channelId];
    const channels =
      channel && idGreater(message.id, channel.lastMessageId)
        ? { ...s.channels, [message.channelId]: { ...channel, lastMessageId: message.id } }
        : s.channels;

    return {
      users,
      messages: next === list ? s.messages : { ...s.messages, [message.channelId]: next },
      channels,
    };
  });
}

function patchMessage(
  set: Setter,
  get: Getter,
  channelId: Snowflake,
  messageId: Snowflake,
  patch: Partial<Message>,
): void {
  patchMessageBy(set, get, channelId, messageId, (message) => ({ ...message, ...patch }));
}

function patchMessageBy(
  set: Setter,
  _get: Getter,
  channelId: Snowflake,
  messageId: Snowflake,
  update: (message: Message) => Message,
): void {
  set((s) => {
    const list = s.messages[channelId];
    if (!list) return {};
    const index = list.findIndex((m) => m.id === messageId);
    if (index < 0) return {};
    const next = [...list];
    next[index] = update(list[index]!);
    return { messages: { ...s.messages, [channelId]: next } };
  });
}

/** Une um lote antigo com o que já está em memória, sem duplicados. */
function mergeMessages(existing: readonly Message[], incoming: readonly Message[]): Message[] {
  if (existing.length === 0) return [...incoming];
  const seen = new Set(existing.map((m) => m.id));
  const merged = [...incoming.filter((m) => !seen.has(m.id)), ...existing];
  return merged.sort((a, b) => idCompare(a.id, b.id));
}

function applyReaction(
  reactions: Message["reactions"],
  emoji: string,
  add: boolean,
  mine: boolean,
): Message["reactions"] {
  const existing = reactions.find((r) => r.emoji === emoji);

  if (!existing) return add ? [...reactions, { emoji, count: 1, me: mine }] : [...reactions];

  const count = Math.max(0, existing.count + (add ? 1 : -1));
  if (count === 0) return reactions.filter((r) => r.emoji !== emoji);

  return reactions.map((r) => (r.emoji === emoji ? { emoji, count, me: mine ? add : r.me } : r));
}
